import _asyncToGenerator from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/vercel/path0/node_modules/@babel/runtime/regenerator/index.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useRef, useMemo, useCallback, useDebugValue } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim/index.js';
import { OBJECT, SWRConfig as SWRConfig$1, defaultConfig, withArgs, SWRGlobalState, serialize, createCacheHelper, isUndefined, getTimestamp, UNDEFINED, isFunction, internalMutate, useIsomorphicLayoutEffect, subscribeCallback, IS_SERVER, rAF, IS_REACT_LEGACY, mergeObjects, revalidateEvents } from 'swr/_internal';
export { mutate, preload, useSWRConfig } from 'swr/_internal';
var WITH_DEDUPE = {
  dedupe: true
};

var useSWRHandler = function useSWRHandler(_key, fetcher, config) {
  var cache = config.cache,
      compare = config.compare,
      suspense = config.suspense,
      fallbackData = config.fallbackData,
      revalidateOnMount = config.revalidateOnMount,
      refreshInterval = config.refreshInterval,
      refreshWhenHidden = config.refreshWhenHidden,
      refreshWhenOffline = config.refreshWhenOffline,
      keepPreviousData = config.keepPreviousData;

  var _SWRGlobalState$get = SWRGlobalState.get(cache),
      _SWRGlobalState$get2 = _slicedToArray(_SWRGlobalState$get, 3),
      EVENT_REVALIDATORS = _SWRGlobalState$get2[0],
      MUTATION = _SWRGlobalState$get2[1],
      FETCH = _SWRGlobalState$get2[2]; // `key` is the identifier of the SWR `data` state, `keyInfo` holds extra
  // states such as `error` and `isValidating` inside,
  // all of them are derived from `_key`.
  // `fnArg` is the argument/arguments parsed from the key, which will be passed
  // to the fetcher.


  var _serialize = serialize(_key),
      _serialize2 = _slicedToArray(_serialize, 2),
      key = _serialize2[0],
      fnArg = _serialize2[1]; // If it's the initial render of this hook.


  var initialMountedRef = useRef(false); // If the hook is unmounted already. This will be used to prevent some effects
  // to be called after unmounting.

  var unmountedRef = useRef(false); // Refs to keep the key and config.

  var keyRef = useRef(key);
  var fetcherRef = useRef(fetcher);
  var configRef = useRef(config);

  var getConfig = function getConfig() {
    return configRef.current;
  };

  var isActive = function isActive() {
    return getConfig().isVisible() && getConfig().isOnline();
  };

  var _createCacheHelper = createCacheHelper(cache, key),
      _createCacheHelper2 = _slicedToArray(_createCacheHelper, 3),
      getCache = _createCacheHelper2[0],
      setCache = _createCacheHelper2[1],
      subscribeCache = _createCacheHelper2[2];

  var stateDependencies = useRef({}).current; // eslint-disable-next-line react-hooks/exhaustive-deps

  var fallback = isUndefined(fallbackData) ? config.fallback[key] : fallbackData;

  var isEqual = function isEqual(prev, current) {
    var equal = true;

    for (var _ in stateDependencies) {
      var t = _;

      if (!compare(current[t], prev[t])) {
        if (t === 'data' && isUndefined(prev[t])) {
          if (!compare(current[t], returnedData)) {
            equal = false;
          }
        } else {
          equal = false;
        }
      }
    }

    return equal;
  };

  var getSnapshot = useMemo(function () {
    var shouldStartRequest = function () {
      if (!key) return false;
      if (!fetcher) return false; // If `revalidateOnMount` is set, we take the value directly.

      if (!isUndefined(revalidateOnMount)) return revalidateOnMount; // If it's paused, we skip revalidation.

      if (getConfig().isPaused()) return false;
      if (suspense) return false;
      return true;
    }();

    var getSelectedCache = function getSelectedCache() {
      var state = getCache(); // We only select the needed fields from the state.

      var snapshot = mergeObjects(state);
      delete snapshot._k;

      if (!shouldStartRequest) {
        return snapshot;
      }

      return _objectSpread({
        isValidating: true,
        isLoading: true
      }, snapshot);
    };

    var memorizedSnapshot = getSelectedCache();
    return function () {
      var snapshot = getSelectedCache();
      return isEqual(snapshot, memorizedSnapshot) ? memorizedSnapshot : memorizedSnapshot = snapshot;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cache, key]); // Get the current state that SWR should return.

  var cached = useSyncExternalStore(useCallback(function (callback) {
    return subscribeCache(key, function (prev, current) {
      if (!isEqual(prev, current)) callback();
    });
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [cache, key]), getSnapshot, getSnapshot);
  var isInitialMount = !initialMountedRef.current;
  var cachedData = cached.data;
  var data = isUndefined(cachedData) ? fallback : cachedData;
  var error = cached.error; // Use a ref to store previously returned data. Use the initial data as its initial value.

  var laggyDataRef = useRef(data);
  var returnedData = keepPreviousData ? isUndefined(cachedData) ? laggyDataRef.current : cachedData : data; // - Suspense mode and there's stale data for the initial render.
  // - Not suspense mode and there is no fallback data and `revalidateIfStale` is enabled.
  // - `revalidateIfStale` is enabled but `data` is not defined.

  var shouldDoInitialRevalidation = function () {
    // If `revalidateOnMount` is set, we take the value directly.
    if (isInitialMount && !isUndefined(revalidateOnMount)) return revalidateOnMount; // If it's paused, we skip revalidation.

    if (getConfig().isPaused()) return false; // Under suspense mode, it will always fetch on render if there is no
    // stale data so no need to revalidate immediately mount it again.
    // If data exists, only revalidate if `revalidateIfStale` is true.

    if (suspense) return isUndefined(data) ? false : config.revalidateIfStale; // If there is no stale data, we need to revalidate when mount;
    // If `revalidateIfStale` is set to true, we will always revalidate.

    return isUndefined(data) || config.revalidateIfStale;
  }(); // Resolve the default validating state:
  // If it's able to validate, and it should revalidate when mount, this will be true.


  var defaultValidatingState = !!(key && fetcher && isInitialMount && shouldDoInitialRevalidation);
  var isValidating = isUndefined(cached.isValidating) ? defaultValidatingState : cached.isValidating;
  var isLoading = isUndefined(cached.isLoading) ? defaultValidatingState : cached.isLoading; // The revalidation function is a carefully crafted wrapper of the original
  // `fetcher`, to correctly handle the many edge cases.

  var revalidate = useCallback( /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(revalidateOpts) {
      var currentFetcher, newData, startAt, loading, opts, shouldStartNewRequest, callbackSafeguard, finalState, finishRequestAndUpdateState, cleanupState, initialState, _FETCH$key, mutationInfo, cacheData, currentConfig, shouldRetryOnError;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              currentFetcher = fetcherRef.current;

              if (!(!key || !currentFetcher || unmountedRef.current || getConfig().isPaused())) {
                _context.next = 3;
                break;
              }

              return _context.abrupt("return", false);

            case 3:
              loading = true;
              opts = revalidateOpts || {}; // If there is no ongoing concurrent request, or `dedupe` is not set, a
              // new request should be initiated.

              shouldStartNewRequest = !FETCH[key] || !opts.dedupe;
              /*
               For React 17
               Do unmount check for calls:
               If key has changed during the revalidation, or the component has been
               unmounted, old dispatch and old event callbacks should not take any
               effect
               For React 18
              only check if key has changed
              https://github.com/reactwg/react-18/discussions/82
              */

              callbackSafeguard = function callbackSafeguard() {
                if (IS_REACT_LEGACY) {
                  return !unmountedRef.current && key === keyRef.current && initialMountedRef.current;
                }

                return key === keyRef.current;
              }; // The final state object when the request finishes.


              finalState = {
                isValidating: false,
                isLoading: false
              };

              finishRequestAndUpdateState = function finishRequestAndUpdateState() {
                setCache(finalState);
              };

              cleanupState = function cleanupState() {
                // Check if it's still the same request before deleting it.
                var requestInfo = FETCH[key];

                if (requestInfo && requestInfo[1] === startAt) {
                  delete FETCH[key];
                }
              }; // Start fetching. Change the `isValidating` state, update the cache.


              initialState = {
                isValidating: true
              }; // It is in the `isLoading` state, if and only if there is no cached data.
              // This bypasses fallback data and laggy data.

              if (isUndefined(getCache().data)) {
                initialState.isLoading = true;
              }

              _context.prev = 12;

              if (shouldStartNewRequest) {
                setCache(initialState); // If no cache is being rendered currently (it shows a blank page),
                // we trigger the loading slow event.

                if (config.loadingTimeout && isUndefined(getCache().data)) {
                  setTimeout(function () {
                    if (loading && callbackSafeguard()) {
                      getConfig().onLoadingSlow(key, config);
                    }
                  }, config.loadingTimeout);
                } // Start the request and save the timestamp.
                // Key must be truthy if entering here.


                FETCH[key] = [currentFetcher(fnArg), getTimestamp()];
              }

              _FETCH$key = _slicedToArray(FETCH[key], 2);
              newData = _FETCH$key[0];
              startAt = _FETCH$key[1];
              _context.next = 19;
              return newData;

            case 19:
              newData = _context.sent;

              if (shouldStartNewRequest) {
                // If the request isn't interrupted, clean it up after the
                // deduplication interval.
                setTimeout(cleanupState, config.dedupingInterval);
              } // If there're other ongoing request(s), started after the current one,
              // we need to ignore the current one to avoid possible race conditions:
              //   req1------------------>res1        (current one)
              //        req2---------------->res2
              // the request that fired later will always be kept.
              // The timestamp maybe be `undefined` or a number


              if (!(!FETCH[key] || FETCH[key][1] !== startAt)) {
                _context.next = 24;
                break;
              }

              if (shouldStartNewRequest) {
                if (callbackSafeguard()) {
                  getConfig().onDiscarded(key);
                }
              }

              return _context.abrupt("return", false);

            case 24:
              // Clear error.
              finalState.error = UNDEFINED; // If there're other mutations(s), that overlapped with the current revalidation:
              // case 1:
              //   req------------------>res
              //       mutate------>end
              // case 2:
              //         req------------>res
              //   mutate------>end
              // case 3:
              //   req------------------>res
              //       mutate-------...---------->
              // we have to ignore the revalidation result (res) because it's no longer fresh.
              // meanwhile, a new revalidation should be triggered when the mutation ends.

              mutationInfo = MUTATION[key];

              if (!(!isUndefined(mutationInfo) && ( // case 1
              startAt <= mutationInfo[0] || // case 2
              startAt <= mutationInfo[1] || // case 3
              mutationInfo[1] === 0))) {
                _context.next = 30;
                break;
              }

              finishRequestAndUpdateState();

              if (shouldStartNewRequest) {
                if (callbackSafeguard()) {
                  getConfig().onDiscarded(key);
                }
              }

              return _context.abrupt("return", false);

            case 30:
              // Deep compare with the latest state to avoid extra re-renders.
              // For local state, compare and assign.
              cacheData = getCache().data; // Since the compare fn could be custom fn
              // cacheData might be different from newData even when compare fn returns True

              finalState.data = compare(cacheData, newData) ? cacheData : newData; // Trigger the successful callback if it's the original request.

              if (shouldStartNewRequest) {
                if (callbackSafeguard()) {
                  getConfig().onSuccess(newData, key, config);
                }
              }

              _context.next = 41;
              break;

            case 35:
              _context.prev = 35;
              _context.t0 = _context["catch"](12);
              cleanupState();
              currentConfig = getConfig();
              shouldRetryOnError = currentConfig.shouldRetryOnError; // Not paused, we continue handling the error. Otherwise, discard it.

              if (!currentConfig.isPaused()) {
                // Get a new error, don't use deep comparison for errors.
                finalState.error = _context.t0; // Error event and retry logic. Only for the actual request, not
                // deduped ones.

                if (shouldStartNewRequest && callbackSafeguard()) {
                  currentConfig.onError(_context.t0, key, currentConfig);

                  if (shouldRetryOnError === true || isFunction(shouldRetryOnError) && shouldRetryOnError(_context.t0)) {
                    if (isActive()) {
                      // If it's inactive, stop. It will auto-revalidate when
                      // refocusing or reconnecting.
                      // When retrying, deduplication is always enabled.
                      currentConfig.onErrorRetry(_context.t0, key, currentConfig, revalidate, {
                        retryCount: (opts.retryCount || 0) + 1,
                        dedupe: true
                      });
                    }
                  }
                }
              }

            case 41:
              // Mark loading as stopped.
              loading = false; // Update the current hook's state.

              finishRequestAndUpdateState();
              return _context.abrupt("return", true);

            case 44:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, null, [[12, 35]]);
    }));

    return function (_x) {
      return _ref.apply(this, arguments);
    };
  }(), // `setState` is immutable, and `eventsCallback`, `fnArg`, and
  // `keyValidating` are depending on `key`, so we can exclude them from
  // the deps array.
  //
  // FIXME:
  // `fn` and `config` might be changed during the lifecycle,
  // but they might be changed every render like this.
  // `useSWR('key', () => fetch('/api/'), { suspense: true })`
  // So we omit the values from the deps array
  // even though it might cause unexpected behaviors.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [key, cache]); // Similar to the global mutate but bound to the current cache and key.
  // `cache` isn't allowed to change during the lifecycle.
  // eslint-disable-next-line react-hooks/exhaustive-deps

  var boundMutate = useCallback( // Use callback to make sure `keyRef.current` returns latest result every time
  function () {
    for (var _len = arguments.length, args = new Array(_len), _key2 = 0; _key2 < _len; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return internalMutate.apply(void 0, [cache, keyRef.current].concat(args));
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []); // The logic for updating refs.

  useIsomorphicLayoutEffect(function () {
    fetcherRef.current = fetcher;
    configRef.current = config; // Handle laggy data updates. If there's cached data of the current key,
    // it'll be the correct reference.

    if (!isUndefined(cachedData)) {
      laggyDataRef.current = cachedData;
    }
  }); // After mounted or key changed.

  useIsomorphicLayoutEffect(function () {
    if (!key) return;
    var softRevalidate = revalidate.bind(UNDEFINED, WITH_DEDUPE); // Expose revalidators to global event listeners. So we can trigger
    // revalidation from the outside.

    var nextFocusRevalidatedAt = 0;

    var onRevalidate = function onRevalidate(type) {
      if (type == revalidateEvents.FOCUS_EVENT) {
        var now = Date.now();

        if (getConfig().revalidateOnFocus && now > nextFocusRevalidatedAt && isActive()) {
          nextFocusRevalidatedAt = now + getConfig().focusThrottleInterval;
          softRevalidate();
        }
      } else if (type == revalidateEvents.RECONNECT_EVENT) {
        if (getConfig().revalidateOnReconnect && isActive()) {
          softRevalidate();
        }
      } else if (type == revalidateEvents.MUTATE_EVENT) {
        return revalidate();
      }

      return;
    };

    var unsubEvents = subscribeCallback(key, EVENT_REVALIDATORS, onRevalidate); // Mark the component as mounted and update corresponding refs.

    unmountedRef.current = false;
    keyRef.current = key;
    initialMountedRef.current = true; // Keep the original key in the cache.

    setCache({
      _k: fnArg
    }); // Trigger a revalidation.

    if (shouldDoInitialRevalidation) {
      if (isUndefined(data) || IS_SERVER) {
        // Revalidate immediately.
        softRevalidate();
      } else {
        // Delay the revalidate if we have data to return so we won't block
        // rendering.
        rAF(softRevalidate);
      }
    }

    return function () {
      // Mark it as unmounted.
      unmountedRef.current = true;
      unsubEvents();
    };
  }, [key]); // Polling

  useIsomorphicLayoutEffect(function () {
    var timer;

    function next() {
      // Use the passed interval
      // ...or invoke the function with the updated data to get the interval
      var interval = isFunction(refreshInterval) ? refreshInterval(data) : refreshInterval; // We only start the next interval if `refreshInterval` is not 0, and:
      // - `force` is true, which is the start of polling
      // - or `timer` is not 0, which means the effect wasn't canceled

      if (interval && timer !== -1) {
        timer = setTimeout(execute, interval);
      }
    }

    function execute() {
      // Check if it's OK to execute:
      // Only revalidate when the page is visible, online, and not errored.
      if (!getCache().error && (refreshWhenHidden || getConfig().isVisible()) && (refreshWhenOffline || getConfig().isOnline())) {
        revalidate(WITH_DEDUPE).then(next);
      } else {
        // Schedule the next interval to check again.
        next();
      }
    }

    next();
    return function () {
      if (timer) {
        clearTimeout(timer);
        timer = -1;
      }
    };
  }, [refreshInterval, refreshWhenHidden, refreshWhenOffline, key]); // Display debug info in React DevTools.

  useDebugValue(returnedData); // In Suspense mode, we can't return the empty `data` state.
  // If there is an `error`, the `error` needs to be thrown to the error boundary.
  // If there is no `error`, the `revalidation` promise needs to be thrown to
  // the suspense boundary.

  if (suspense && isUndefined(data) && key) {
    // SWR should throw when trying to use Suspense on the server with React 18,
    // without providing any initial data. See:
    // https://github.com/vercel/swr/issues/1832
    if (!IS_REACT_LEGACY && IS_SERVER) {
      throw new Error('Fallback data is required when using suspense in SSR.');
    } // Always update fetcher and config refs even with the Suspense mode.


    fetcherRef.current = fetcher;
    configRef.current = config;
    unmountedRef.current = false;
    throw isUndefined(error) ? revalidate(WITH_DEDUPE) : error;
  }

  return {
    mutate: boundMutate,

    get data() {
      stateDependencies.data = true;
      return returnedData;
    },

    get error() {
      stateDependencies.error = true;
      return error;
    },

    get isValidating() {
      stateDependencies.isValidating = true;
      return isValidating;
    },

    get isLoading() {
      stateDependencies.isLoading = true;
      return isLoading;
    }

  };
};

var SWRConfig = OBJECT.defineProperty(SWRConfig$1, 'defaultValue', {
  value: defaultConfig
});

var unstable_serialize = function unstable_serialize(key) {
  return serialize(key)[0];
};
/**
 * A hook to fetch data.
 *
 * @link https://swr.vercel.app
 * @example
 * ```jsx
 * import useSWR from 'swr'
 * function Profile() {
 *   const { data, error } = useSWR('/api/user', fetcher)
 *   if (error) return <div>failed to load</div>
 *   if (!data) return <div>loading...</div>
 *   return <div>hello {data.name}!</div>
 * }
 * ```
 */


var useSWR = withArgs(useSWRHandler); // useSWR

export { SWRConfig, useSWR as default, unstable_serialize };